// packages/shared/src/schemas/site-settings.ts
import { z } from 'zod'

const optionalUrl = z
  .string()
  .trim()
  .refine((v) => v === '' || /^https?:\/\/.+/i.test(v), 'Must be a valid http(s) URL, or left empty')

const jsonField = (label: string) =>
  z.string().trim().refine((v) => {
    if (v === '') return true
    try {
      JSON.parse(v)
      return true
    } catch {
      return false
    }
  }, `${label} must be valid JSON, or left empty`)

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/

export const SiteConfigInput = z.object({
  brand_name: z.string().min(1, 'Brand name is required').max(120).trim(),
  brand_tagline: z.string().min(1, 'Tagline is required').max(160).trim(),
  logo_url: optionalUrl,
  favicon_url: optionalUrl,
  announcement_ticker: z.string().max(300).trim(),
  guarantee_badge_text: z.string().max(200).trim(),

  hero_title_line1: z.string().max(120).trim(),
  hero_title_line2: z.string().max(120).trim(),
  hero_subtext: z.string().max(500).trim(),
  hero_cta_primary: z.string().max(60).trim(),
  hero_cta_secondary: z.string().max(60).trim(),
  hero_stat1_value: z.string().max(40).trim(),
  hero_stat1_label: z.string().max(60).trim(),
  hero_stat2_value: z.string().max(40).trim(),
  hero_stat2_label: z.string().max(60).trim(),
  hero_stat3_value: z.string().max(40).trim(),
  hero_stat3_label: z.string().max(60).trim(),
  hero_showcase_badge: z.string().max(80).trim(),
  hero_showcase_title: z.string().max(120).trim(),
  hero_showcase_price: z.string().max(40).trim(),
  hero_showcase_image: optionalUrl,

  category_section_title: z.string().max(120).trim(),
  category_section_subtitle: z.string().max(200).trim(),
  catalogue_section_title: z.string().max(120).trim(),
  catalogue_section_subtitle: z.string().max(200).trim(),

  vip_badge_text: z.string().max(80).trim(),
  vip_title: z.string().max(150).trim(),
  vip_subtext: z.string().max(300).trim(),
  vip_button_text: z.string().max(40).trim(),

  prop1_title: z.string().max(80).trim(),
  prop1_subtext: z.string().max(150).trim(),
  prop2_title: z.string().max(80).trim(),
  prop2_subtext: z.string().max(150).trim(),
  prop3_title: z.string().max(80).trim(),
  prop3_subtext: z.string().max(150).trim(),
  prop4_title: z.string().max(80).trim(),
  prop4_subtext: z.string().max(150).trim(),

  categories_json: jsonField('Legacy Store Categories JSON'),
  footer_care_links_json: jsonField('Footer Care Links JSON'),
  footer_collections_title: z.string().max(80).trim(),
  footer_care_title: z.string().max(80).trim(),
  footer_brand_blurb: z.string().max(400).trim(),

  company_legal_name: z.string().max(200).trim(),
  gst_enabled: z.enum(['true', 'false']),
  gstin_number: z.string().trim(),

  metal_rates_json: jsonField('Metal Rates JSON'),
  contact_phone: z.string().max(30).trim(),
  contact_email: z
    .string()
    .trim()
    .refine((v) => v === '' || z.string().email().safeParse(v).success, 'Enter a valid email address'),
  store_address: z.string().max(300).trim(),

  free_delivery_min_amount: z.string().trim().regex(/^\d+(\.\d{1,2})?$/, 'Enter a rupee amount, e.g. 499').default('499'),
  standard_shipping_amount: z.string().trim().regex(/^\d+(\.\d{1,2})?$/, 'Enter a rupee amount, e.g. 49').default('49'),
}).superRefine((data, ctx) => {
  if (data.gst_enabled === 'true' && !GSTIN_REGEX.test(data.gstin_number.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Enter a valid 15-character GSTIN (e.g. 33AAAAA0000A1Z5) — required when GST invoicing is enabled',
      path: ['gstin_number'],
    })
  }
})

export type SiteConfigInput = z.infer<typeof SiteConfigInput>
